import get from 'lodash/get';
import isNumber from 'lodash/isNumber';
import omit from 'lodash/omit';
import sample from 'lodash/sample';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useState } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';

import { getLocationOptions } from '../../helpers/locations';
import {
  getLocalTimeZone,
  getTimeZoneOptions,
  validateAvailabilityTimes
} from '../../helpers/scheduling';
import { getDays } from '../../helpers/scheduling';
import AvailabilityInput from './AvailabilityInput';
import CustomBox from '../common/CustomBox';
import ColorPicker, { colorOptions } from '../common/ColorPicker';
import CustomNumberField from '../common/CustomNumberField';
import CustomSelect from '../common/CustomSelect';
import CustomTextField from '../common/CustomTextField';
import CurrencyField from '../common/CurrencyField';
import DateRangePicker from '../scheduling/DateRangePicker';
import FormSectionHeading from '../common/FormSectionHeading';
import HtmlEditor from '../common/HtmlEditor';
import StateSelect from '../common/StateSelect';
import BackButton from "../common/BackButton";
import './SessionTypeForm.scss';
import { displayName } from 'react-quill';


const locationsWithAddresses = [
  'PARTNER',
  // 'PROVIDER_HOME',
  // 'GYM',
  // 'WORK',
];

const getFields = () => ([
  {
    name: 'title',
    label: 'Session type',
    displayName: 'Session type name',
    type: 'text',
    placeholder: 'Enter name...',
    defaultValue: '',
    required: true,
    hideIfAvailabilityHidden: false,
    validate: str => !!str,
    appendDivider: false,
    gridItem: 6,
  },
  {
    name: 'location_type',
    label: 'Location type',
    displayName: 'Location type',
    type: 'options',
    placeholder: 'Add a location',
    required: true,
    hideIfAvailabilityHidden: false,
    validate: val => !!val,
    appendDivider: false,
    options: [...getLocationOptions()],
    gridItem: 6,
  },
  {
    name: 'is_physical_therapy',
    label: 'This is a restore session',
    displayName: 'Is a restore session',
    type: 'boolean',
    placeholder: null,
    defaultValue: true,
    required: false,
    hideIfAvailabilityHidden: false,
    validate: () => true,
    appendDivider: false,
    gridItem: 12,
  },
  {
    name: 'pinned',
    label: 'Pin this session type to the top of the list',
    displayName: 'Pinned',
    type: 'boolean',
    placeholder: null,
    defaultValue: false,
    required: false,
    hideIfAvailabilityHidden: false,
    validate: () => true,
    appendDivider: false,
    gridItem: 12,
  },
  // {
  //   name: 'is_discovery_session',
  //   label: 'This is a discovery session',
  //   displayName: 'Is a discovery session',
  //   type: 'boolean',
  //   placeholder: null,
  //   defaultValue: false,
  //   required: false,
  //   hideIfAvailabilityHidden: false,
  //   validate: () => true,
  //   appendDivider: false,
  //   gridItem: 12,
  // },
  {
    name: 'address_line_1',
    label: 'Address Line 1',
    displayName: 'Address Line 1',
    type: 'text',
    placeholder: '123 Main St',
    defaultValue: '',
    required: false,
    hideIfAvailabilityHidden: false,
    requiredForAddress: true,
    validate: val => !!val,
    appendDivider: false,
    hideIfLocationIsNotFixed: true,
    gridItem: 12,
  },
  {
    name: 'address_line_2',
    label: 'Address Line 2',
    displayName: 'Address Line 2',
    type: 'text',
    placeholder: 'Suite #1',
    defaultValue: '',
    required: false,
    hideIfAvailabilityHidden: false,
    requiredForAddress: false,
    validate: () => true,
    appendDivider: false,
    hideIfLocationIsNotFixed: true,
    gridItem: 12,
  },
  {
    name: 'address_city',
    label: 'City',
    displayName: 'City',
    type: 'text',
    placeholder: 'Arlington',
    defaultValue: '',
    required: false,
    hideIfAvailabilityHidden: false,
    requiredForAddress: true,
    validate: val => !!val,
    appendDivider: false,
    hideIfLocationIsNotFixed: true,
    gridItem: 4,
  },
  {
    name: 'address_zip',
    label: 'Zip Code',
    displayName: 'Zip Code',
    type: 'text',
    placeholder: '54321',
    defaultValue: '',
    required: false,
    hideIfAvailabilityHidden: false,
    requiredForAddress: true,
    validate: val => /^[\d]{5,5}$/.test(val),
    appendDivider: false,
    hideIfLocationIsNotFixed: true,
    gridItem: 4,
  },
  {
    name: 'address_state',
    label: 'State',
    displayName: 'State',
    type: 'stateselect',
    placeholder: 'VA',
    required: false,
    hideIfAvailabilityHidden: false,
    requiredForAddress: true,
    validate: () => true,
    appendDivider: false,
    hideIfLocationIsNotFixed: true,
    gridItem: 4,
  },
  {
    name: 'description',
    label: 'Description',
    displayName: 'Description',
    type: 'textarea',
    placeholder: 'Add a description...',
    defaultValue: '',
    required: false,
    appendDivider: false,
    hideIfAvailabilityHidden: false,
    validate: str => !!str,
    gridItem: 12,
  },
  {
    name: 'instructions',
    label: 'Instructions',
    displayName: 'Instructions',
    type: 'textarea',
    placeholder: 'Add instructions for the patient (For example: parking, building entry, etc.)...',
    defaultValue: '',
    required: false,
    appendDivider: false,
    hideIfAvailabilityHidden: false,
    validate: str => !!str,
    gridItem: 12,
  },
  {
    name: 'duration_minutes',
    label: 'Session duration (minutes)',
    displayName: 'Session duration (minutes)',
    type: 'number',
    placeholder: '30',
    defaultValue: '30',
    InputProps: { inputProps: { type: 'text', min: '0', max: '999', step: '1', maxlength: '3' } },
    required: true,
    requiredForScheduling: true,
    hideIfAvailabilityHidden: false,
    validate: str => !!str,
    gridItem: 6,
  },
  {
    name: 'hourly_rate',
    label: 'Session rate / hour',
    displayName: 'Session hourly rate',
    type: 'currency',
    placeholder: '120.00',
    defaultValue: '',
    InputProps: { inputProps: { type: 'text', min: '0', max: '999', step: '0.01', maxlength: '6' } },
    required: true,
    requiredForScheduling: true,
    hideIfAvailabilityHidden: false,
    validate: str => !!str || ['0', 0].includes(str),
    appendDivider: false,
    gridItem: 6,
  },
  {
    name: 'hide_availability',
    label: 'Do not show your availability (patients will reach out to you directly to schedule)',
    displayName: 'Hide availability',
    type: 'boolean',
    defaultValue: false,
    required: false,
    hideIfAvailabilityHidden: false,
    validate: () => true,
    prepend: () => (
      <FormSectionHeading
        title='Availability for this session type'
        subtitle='Define custom hours specific to this type of event.'
      />
    ),
    gridItem: 12,
  },
  {
    name: 'availabilities',
    label: 'Set your weekly hours',
    displayName: 'Weekly availability',
    type: 'availability',
    defaultValue: getDays().reduce((acc, d) => ([
      ...acc,
      ...(
        ['SAT', 'SUN'].includes(d)
          ? []
          : [
            { day: d, start_time: '09:00 am', end_time: '12:00 pm' },
            { day: d, start_time: '12:30 pm', end_time: '5:00 pm' },
          ]
      )]),
      []
    ),
    placeholder: null,
    required: false,
    value: null,
    requiredForScheduling: true,
    hideIfAvailabilityHidden: true,
    validate: values => validateAvailabilityTimes(values),
    gridItem: 6,
  },
  {
    name: 'timezone',
    label: 'Select your time zone',
    displayName: 'Time zone',
    type: 'options',
    placeholder: null,
    appendDivider: false,
    required: false,
    requiredForScheduling: true,
    hideIfAvailabilityHidden: false,
    defaultValue: getLocalTimeZone(),
    validate: () => true,
    options: getTimeZoneOptions(),
    gridItem: 12,
  },
  {
    name: 'date_range',
    type: 'daterange',
    label: 'Date range',
    displayName: 'Date range',
    helperText: 'Set a range of dates when you can accept sessions.',
    required: false,
    defaultValue: null,
    appendDivider: false,
    hideIfAvailabilityHidden: false,
    validate: () => true,
    prepend: () => (
      <FormSectionHeading
        title='Date Range'
        subtitle='Set a range of dates when you can accept sessions.'
      />
    ),
  },
  {
    name: 'scheduling_window_hours',
    label: null,
    displayName: 'Scheduling window hours',
    type: 'options',
    helperText: null,
    placeholder: null,
    appendDivider: false,
    required: false,
    hideIfAvailabilityHidden: true,
    validate: () => true,
    options: [
      { value: '1', label: '1 hour' },
      { value: '2', label: '2 hours' },
      { value: '4', label: '4 hours' },
      { value: '6', label: '6 hours' },
      { value: '8', label: '8 hours' },
      { value: '12', label: '12 hours' },
      { value: '24', label: '24 hours' },
      { value: '48', label: '2 days' },
      { value: '72', label: '3 days' },
      { value: '120', label: '5 days' },
      { value: '168', label: '7 days' },
    ],
    prepend: () => (
      <FormSectionHeading
        title='Scheduling window'
        subtitle='How much advanced notice do you require for a patient to book a session?'
      />
    ),
    gridItem: 12,
  },
  {
    name: 'availability_frequency_minutes',
    label: null,
    displayName: 'Session Intervals',
    type: 'options',
    helperText: null,
    placeholder: null,
    defaultValue: '15',
    appendDivider: false,
    required: false,
    hideIfAvailabilityHidden: true,
    validate: () => true,
    options: [
      { value: '15', label: 'Every 15 minutes' },
      { value: '30', label: 'Every 30 minutes' },
      { value: '60', label: 'Every 60 minutes' },
    ],
    prepend: () => (
      <FormSectionHeading
        title='Session Intervals'
        subtitle='At what interval do you want session start times to be available?'
      />
    ),
    gridItem: 12,
  },
  {
    name: 'time_buffer_before_minutes',
    label: 'Before session',
    displayName: 'Buffer time before session',
    type: 'options',
    helperText: null,
    placeholder: null,
    appendDivider: false,
    required: false,
    hideIfAvailabilityHidden: true,
    validate: () => true,
    options: [
      { value: '0', label: 'None' },
      { value: '5', label: '5 minutes' },
      { value: '10', label: '10 minutes' },
      { value: '15', label: '15 minutes' },
      { value: '20', label: '20 minutes' },
      { value: '25', label: '25 minutes' },
      { value: '30', label: '30 minutes' },
      { value: '45', label: '45 minutes' },
      { value: '60', label: '60 minutes' },
      { value: '90', label: '90 minutes' },
    ],
    prepend: () => (
      <FormSectionHeading
        title='Want to add time before or after your session?'
        subtitle='Give yourself some buffer time to prepare for or wrap up from booked session.'
      />
    ),
    gridItem: 12,
  },
  {
    name: 'time_buffer_after_minutes',
    label: 'After session',
    displayName: 'Buffer time after session',
    type: 'options',
    helperText: null,
    placeholder: null,
    appendDivider: false,
    required: false,
    hideIfAvailabilityHidden: true,
    validate: () => true,
    options: [
      { value: '0', label: 'None' },
      { value: '5', label: '5 minutes' },
      { value: '10', label: '10 minutes' },
      { value: '15', label: '15 minutes' },
      { value: '20', label: '20 minutes' },
      { value: '25', label: '25 minutes' },
      { value: '30', label: '30 minutes' },
      { value: '45', label: '45 minutes' },
      { value: '60', label: '60 minutes' },
      { value: '90', label: '90 minutes' },
    ],
    gridItem: 12,
  },
  {
    name: 'maximum_bookings_per_day',
    label: null,
    displayName: 'Maximum bookings per day',
    type: 'options',
    helperText: null,
    placeholder: null,
    appendDivider: false,
    required: false,
    hideIfAvailabilityHidden: true,
    validate: () => true,
    options: [
      { value: '1', label: '1' },
      { value: '2', label: '2' },
      { value: '3', label: '3' },
      { value: '4', label: '4' },
      { value: '5', label: '5' },
      { value: '6', label: '6' },
      { value: '7', label: '7' },
      { value: '8', label: '8' },
      { value: '9', label: '9' },
      { value: '10', label: '10' },
      { value: '11', label: '11' },
      { value: '12', label: '12' },
      {value: '1000', label: 'No limit' },
    ],
    prepend: () => (
      <FormSectionHeading
        title='Maximum bookings per day'
        subtitle='Limit how many booked sessions to accept in a single day'
      />
    ),
    gridItem: 12,
  },
  {
    name: 'send_provider_email',
    label: 'Provider Calendar Invitations',
    displayName: 'Send me calendar invitations',
    type: 'boolean',
    defaultValue: true,
    required: false,
    hideIfAvailabilityHidden: false,
    validate: () => true,
    appendDivider: false,
    prepend: () => (
      <FormSectionHeading
        title='Session invitations and reminders'
        subtitle='Set up automatic session invitations and reminders.'
      />
    )
  },
  {
    name: 'send_patient_email',
    label: 'Client Calendar Invitations',
    displayName: 'Send the client calendar invitations',
    type: 'boolean',
    defaultValue: true,
    required: false,
    hideIfAvailabilityHidden: false,
    validate: () => true,
    appendDivider: true,
  },
  // {
  //   name: 'email_reminders',
  //   label: 'Email Reminders',
  //   type: 'boolean',
  //   defaultValue: false,
  //   required: false,
  //   hideIfAvailabilityHidden: true,
  //   validate: () => true,
  // },
  // {
  //   name: 'text_reminders',
  //   label: 'Text Reminders',
  //   type: 'boolean',
  //   defaultValue: false,
  //   required: false,
  //   appendDivider: true,
  //   hideIfAvailabilityHidden: true,
  //   validate: () => true,
  // },
  {
    name: 'session_meeting_url',
    label: 'Session link',
    displayName: 'Session meeting link',
    type: 'text',
    helperText: 'Add a URL for virtual meetings (starting with https://)',
    placeholder: 'https://meet.google.com/abc-defg-hij',
    appendDivider: false,
    required: false,
    hideIfAvailabilityHidden: false,
    virtualOnly: true,
    validate: str => !!str && /^https:\/\//.test(str),
  },
  {
    name: 'carryforward_only_my_own_notes',
    label: 'When drafting new session notes, use data only from my previous sessions (not those from other providers)',
    displayName: 'Carry forward only my own session notes',
    type: 'boolean',
    defaultValue: false,
    required: false,
    hideIfAvailabilityHidden: false,
    validate: () => true,
    prepend: () => (
      <FormSectionHeading
        title='Carry forward only my session notes'
      />
    )
  },
  // {
  //   name: 'css_color',
  //   label: 'Event color',
  //   displayName: 'Event color',
  //   type: 'colorPicker',
  //   required: false,
  //   defaultValue: sample(colorOptions()),
  //   hideIfAvailabilityHidden: false,
  //   validate: () => true,
  // }
]);

const FieldInput = props => {
  const [blurred, setBlurred] = useState(false);
  const childProps = {
    ...props,
    error: !props.validate(props.value) && blurred,
    // onBlur: () => setBlurred(true),
  };
  if (props.hidden) return <></>;
  switch (props.type) {
    case 'text':
      return <CustomTextField {...childProps} />;
    case 'textarea':
      return (
        <CustomTextField
          inputType='textarea'
          rows={5}
          multiline
          {...childProps}
        />
      );
    case 'number':
      return (
        <CustomTextField
          type='text'
          {...childProps}
        />
      );
    case 'currency':
      return (
        <CurrencyField
          {...childProps}
        />
      );
    case 'options':
      return <CustomSelect {...childProps} />;
    case 'html':
      return <HtmlEditor {...childProps} />;
    case 'availability':
      return (
        <AvailabilityInput
          {...childProps}
          value={props.value}
          defaultValue={childProps.value || childProps.defaultValue}
        />
      );
    case 'boolean':
      return (
        <FormGroup>
          <FormControlLabel
            id={childProps.name}
            required={childProps.required}
            control={
              <Checkbox
                checked={childProps.value}
                defaultChecked={childProps.defaultValue || false}
                onChange={e => props.onChange({
                  target: { value: e.target.checked, checked: e.target.checked }
                })}
                sx={{
                  color: 'text.gray',
                }}
              />
            }
            label={childProps.label}
          />
          <FormHelperText>
            <Typography
              variant='body1'
              color='text.primary'

            >
              {childProps.helperText}
            </Typography>
          </FormHelperText>
        </FormGroup>
      );
    case 'stateselect':
      return (
        <StateSelect {...childProps} />
      );
    case 'daterange':
      return (
        <DateRangePicker
          startDate={
            get(props, 'value.start')
              ? moment(props.value.start)
              : null
          }
          endDate={
            get(props, 'value.end')
              ? moment(props.value.end)
              : null
          }
          value={props.value}
          onChange={props.onChange}
        />
      );
    // case 'colorPicker':
    //   return (
    //     <Box style={{ padding: '1em 0em 1em 0em' }}>
    //       <Typography variant='formLabel'>
    //         {childProps.label}
    //       </Typography>
    //       <Stack direction='row' style={{ marginTop: '1em' }}>
    //         <ColorPicker
    //           defaultValue={props.value || childProps.defaultValue}
    //           onChange={props.onChange}
    //         />
    //       </Stack>
    //     </Box>
    //   );
    default:
      return <></>;
  }
};

// SessionTypeForm component ==========================================

const handleFieldUpdate = (formValues, setFormValues) => fieldName => e => {
  let value = get(e, 'target.value', '');
  const values = {
    ...formValues,
    [fieldName]: value,
  };
  return setFormValues(values);
};

const getDefaultValues = () => getFields().reduce((acc, next) => {
  let value;
  switch (next.type) {
    case 'options':
      value = next.defaultValue || [];
      break;
    case 'boolean':
      value = next.defaultValue === undefined ? false : next.defaultValue;
      break;
    default:
      value = next.defaultValue === undefined ? '' : next.defaultValue;
      break;
  }
  return {
    ...acc,
    [next.name]: value,
  };
}, {});

const parseDefaultValues = (sessionType, template) => {
  console.log('sessionType.defaultValues', sessionType);
  if (!sessionType && !template) return null;
  const source = sessionType || {...template, availabilities: []};
  const excludedKeys = ['date_range_start', 'date_range_end'];
  const date_range = (
    source && source.date_range_start
    ? {
      start: moment(source.date_range_start).format('MM/DD/yyyy'),
      end: moment(source.date_range_end).format('MM/DD/yyyy'),
    }
    : null
  );
  let initialState = date_range ? {date_range} : {};
  let defaultValues = Object.keys(source).reduce((acc, k) => {
    if (excludedKeys.includes(k)) return acc;
    let value = source[k];
    if (isNumber(value)) value = value.toString();
    return { ...acc, [k]: value };
  }, initialState);
  console.log('defaultValues', defaultValues);
  return defaultValues;
};

const parseFormValues = formValues => {
  console.log('formValues', formValues);
  const newValues = {
    ...omit(formValues, 'date_range'),
    // is_physical_therapy: formValues.is_physical_therapy,
    // hide_availability: formValues.hide_availability,
    // location_type: (
    //   formValues.location_type.length
    //   ? formValues.location_type
    //   : null
    // ),
    hourly_rate: (
      formValues.hourly_rate || ['0', 0].includes(formValues.hourly_rate)
        ? parseFloat(formValues.hourly_rate)
        : null
    ),
    duration_minutes: (
      formValues.duration_minutes
        ? parseInt(formValues.duration_minutes)
        : null
    ),
    maximum_bookings_per_day: (
      get(formValues, 'maximum_bookings_per_day.length', 0)
        ? parseInt(formValues.maximum_bookings_per_day, 10)
        : null
    ),
    scheduling_window_hours: (
      get(formValues, 'scheduling_window_hours.length', 0)
        ? parseInt(formValues.scheduling_window_hours, 10)
        : null
    ),
    time_buffer_after_minutes: (
      get(formValues, 'time_buffer_after_minutes.length', 0)
        ? parseInt(formValues.time_buffer_after_minutes, 10)
        : null
    ),
    time_buffer_before_minutes: (
      get(formValues, 'time_buffer_before_minutes.length', 0)
        ? parseInt(formValues.time_buffer_before_minutes, 10)
        : null
    ),
    date_range_start: (
      formValues.date_range?.start
      ? moment(formValues.date_range.start, 'MM/DD/yyyy').format('yyyy-MM-DD')
      : null
    ),
    date_range_end: (
      formValues.date_range?.end
      ? moment(formValues.date_range.end , 'MM/DD/yyyy').format('yyyy-MM-DD')
      : null
    ),
  };
  return newValues;
};

const inputIsVisible = ({ formValues, field }) => {
  if (formValues.hide_availability && field.hideIfAvailabilityHidden) {
    return false;
  }
  if (
    !locationsWithAddresses.includes(formValues.location_type)
    && field.hideIfLocationIsNotFixed
  ) {
    return false;
  }
  if (formValues.location_type !== 'VIRTUAL' && field.virtualOnly) {
    return false;
  }
  return true;
};

const validate = ({ setValidations, formValues, fields }) => {
  const validations = fields.reduce((acc, f) => {
    const value = formValues[f.name];
    // check for all required fields
    if ([null, undefined, '', []].includes(value) && f.required) {
      return [
        ...acc,
        { name: f.name, message: `Missing value for ${f.displayName}.` }
      ];
    }
    // addresses must be included if the location type requires addresses
    if (
      locationsWithAddresses.includes(formValues.location_type)
      && /^address/.test(f.name)
      && f.requiredForAddress
      && !f.validate(value)
    ) {
      return [
        ...acc,
        { name: f.name, message: `Missing value for address (${f.displayName}).` }
      ];
    }
    if (
      !formValues.hide_availability
      && f.requiredForScheduling
      && !f.validate(value)
    ) {
      return [
        ...acc,
        { name: f.name, message: `Please fill in required scheduling field: ${f.displayName}` }
      ];
    }
    if (!f.required && [null, undefined, '', []].includes(value)) {
      return acc;
    }
    // if present, validate the value
    if (!f.validate(value)) {
      return [
        ...acc,
        { name: f.name, message: `Please enter a valid value for ${f.displayName}.` }
      ];
    }
    return acc;
  }, []);
  setValidations(validations);
  return validations;
};

const SessionTypeForm = props => {
  const defaultValues = (
    parseDefaultValues(props.sessionType, props?.template)
    || getDefaultValues()
  );
  const [formValues, setFormValues] = useState(defaultValues);
  // eslint-disable-next-line no-unused-vars
  const [validations, setValidations] = useState([]);
  const fields = getFields({});
  return (
    <Box className='SessionTypeForm'>
      <Box className='SessionTypeForm__head'>
        <Box className='SessionTypeForm__head__nav'>
          <BackButton />
        </Box>
        {
          props.warning
          &&
          <Stack spacing={2} padding={{xs: '0 0 15px 0', sm: '20px 0'}}>
            <Alert severity='warning'>
              <AlertTitle>Warning</AlertTitle>
              {get(props, 'warning.message', 'Unknown warning')}
            </Alert>
          </Stack>
        }
        <Box className='SessionTypeForm__head__info-box'>
          <Typography variant='body1' color='text.primary' style={{
            textAlign: 'center',
            fontSize: '1.125rem',
            fontStyle: 'normal',
            fontWeight: 600,
            lineHeight: '1.875rem'
          }}>

            {props.sessionType ? 'Edit ' : 'New '}Session Type
          </Typography>
          <Box className='SessionTypeForm__head__line'></Box>
        </Box>
      </Box>

      <CustomBox borderless>
        <Box className='ProfileSettings__form'>
          <Grid container spacing={2}>
            {fields.map(f =>
              <Grid
                key={f.name} item xs={12} md={f.gridItem ? f.gridItem : 12}
                className={!inputIsVisible({ formValues, field: f }) ? 'hide' : ''}
              >
                {f.prepend && inputIsVisible({ formValues, field: f })
                  ? f.prepend()
                  : <></>
                }
                <FieldInput
                  fullWidth
                  onChange={handleFieldUpdate(formValues, setFormValues)(f.name)}
                  hidden={!inputIsVisible({ formValues, field: f })}
                  {...f}
                  defaultValue={get(defaultValues, f.name)}
                  value={get(formValues, f.name)}
                />
                {f.appendDivider && inputIsVisible({ formValues, field: f })
                  ? <Divider />
                  : <></>
                }
              </Grid>
            )}
            <Grid item xs={12}>
              <Stack direction='row' justifyContent='center' spacing={2} style={{ marginBottom: 20 }}>
                {
                  props.onCancel
                    ? <Button
                      size='large'
                      variant='primaryOutline'
                      onClick={props.onCancel}
                    >
                      Cancel
                    </Button>
                    : null
                }
                <Button
                  variant='primaryGradient'
                  size='large'
                  onClick={() => {
                    const _validations = validate({
                      setValidations,
                      fields,
                      formValues
                    });
                    if (_validations.length) {
                      props.setError(new Error(_validations.reduce((acc, v) => (
                        acc + v.message + '\n'
                      ), 'The following fields are invalid: \n')
                      ))
                    }
                    if (!_validations.length) {
                      props.onSubmit(parseFormValues(formValues));
                    }
                  }}
                >
                  Save Changes
                </Button>
              </Stack>
            </Grid>

          </Grid>
        </Box>
      </CustomBox>
    </Box>
  );
};

SessionTypeForm.defaultProps = {
  isEditingExistingSessionType: false,
};

SessionTypeForm.propTypes = {
  onCancel: PropTypes.func,
  // providerAvailabilities: PropTypes.arrayOf(PropTypes.shape({
  //   session_type_id: PropTypes.string,
  //   day_of_week: PropTypes.string,
  //   start: PropTypes.string,
  //   end: PropTypes.string,
  // })),
  isEditingExistingSessionType: PropTypes.bool,
};

export default SessionTypeForm;
